import React, { type JSX } from 'react';
import { InfoListItem, ListItemTag } from '@brightlayer-ui/react-components';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import NotificationsActive from '@mui/icons-material/NotificationsActive';
import Warning from '@mui/icons-material/Warning';
import { Divider, Paper, Stack, Typography } from '@mui/material';
import { WidgetFooter } from 'pages/WidgetManagement/common';

type Metric = {
    id?: string;
    name: string;
    value: number | string;
    unit?: string;
    severity?: 'alarm' | 'warning' | 'normal';
    status?: 'warning' | 'normal';
    aggregation?: string;
    displayType?: string;
};

type Notification = {
    id: string | number;
    title?: string;
    displayName?: string;
    value: number | string;
    unit?: string;
    occurredAt?: string;
    time?: string;
    date?: string;
    type?: 'alarm' | 'warning' | 'offline' | 'normal';
};

type EnvironmentalStressMonitoringWidgetData = {
    widgetName?: string;
    overview?: {
        title?: string;
        status?: { label?: string; severity?: 'alarm' | 'warning' | 'normal' };
        metrics?: Metric[];
    };
    secondary?: Notification[];
    statusCount?: { alarms?: number; warnings?: number };
    maxCount?: number;
    total?: number;
    viewAllButton?: boolean;
    viewTitleBar?: boolean;
    showWidgetTitleBar?: boolean;
};

type EnvironmentalStressMonitoringProps = {
    widgetData?: EnvironmentalStressMonitoringWidgetData;
    mode?: string;
};

type NotificationStatusKey = keyof NonNullable<EnvironmentalStressMonitoringWidgetData['statusCount']>;

const notificationStatuses: ReadonlyArray<{ key: NotificationStatusKey; label: string }> = [
    { key: 'alarms', label: 'Alarm' },
    { key: 'warnings', label: 'Warning' },
];

const formatOccurredAt = (occurredAt?: string): { time: string; date: string } => {
    if (!occurredAt) return { time: '--', date: '' };

    const date = new Date(occurredAt);
    if (Number.isNaN(date.getTime())) return { time: '--', date: '' };

    return {
        time: date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }),
        date: date.toLocaleDateString(),
    };
};

const getNotificationIcon = (type?: Notification['type']): JSX.Element =>
    type === 'alarm' ? (
        <NotificationsActive color="error" sx={{ fontSize: 22 }} />
    ) : (
        <Warning color="warning" sx={{ fontSize: 22 }} />
    );

const formatNotificationCounts = (statusCount?: EnvironmentalStressMonitoringWidgetData['statusCount']): string =>
    notificationStatuses
        .map(({ key, label }) => {
            const count = statusCount?.[key] ?? 0;
            return `${count} ${label}${count === 1 ? '' : 's'}`;
        })
        .join(', ');

const WidgetHeaderTitle = ({ widgetName, mode }: { widgetName?: string; mode?: string }): JSX.Element => (
    <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        ml={1}
        sx={
            mode === 'edit'
                ? { position: 'absolute', top: '0px', height: '48px', left: '50px' }
                : { height: '48px', padding: '16px' }
        }
    >
        <Typography variant="subtitle2" color="primary" fontWeight={600} fontSize="14px">
            {widgetName}
        </Typography>
    </Stack>
);

const Component = ({ widgetData = {}, mode }: EnvironmentalStressMonitoringProps): JSX.Element => {
    const overview = widgetData.overview ?? {};
    const metrics = overview.metrics ?? [];
    const notifications = widgetData.secondary ?? [];
    const visibleNotifications = notifications.slice(0, widgetData.maxCount ?? notifications.length);
    const showTitleBar = widgetData.showWidgetTitleBar ?? widgetData.viewTitleBar ?? true;
    return (
        <Paper sx={{ height: '100%', overflow: 'hidden' }} data-testid="environmental-stress-monitoring-widget">
            {showTitleBar && <WidgetHeaderTitle widgetName={widgetData.widgetName} mode={mode} />}
            {showTitleBar && mode !== 'edit' && <Divider />}
            <Stack sx={{ height: 'calc(100% - 100px)', overflow: 'auto' }}>
                <Stack height={48} px={3} justifyContent="center" className="border-bottom-1">
                    <Typography
                        color="#424E54"
                        fontFamily="Open Sans"
                        fontSize={16}
                        fontWeight={600}
                        sx={{ fontFeatureSettings: "'liga' off, 'clig' off" }}
                    >
                        {overview.title ?? '30-Day Overview'}
                    </Typography>
                </Stack>
                <Stack
                    height={147}
                    px="3.2px"
                    py="20.5px"
                    direction="row"
                    alignItems="center"
                    gap="16.562px"
                    className="border-bottom-1"
                >
                    <Stack
                        alignItems="center"
                        justifyContent="center"
                        width={125.3}
                        height={106}
                        sx={{ borderRight: 1, borderColor: 'divider' }}
                    >
                        <Warning color="warning" sx={{ fontSize: 33, mb: 0.5 }} />
                        <Typography
                            color="#556167"
                            fontFamily="Open Sans"
                            fontSize={18}
                            fontWeight={600}
                            textAlign="center"
                        >
                            {overview.status?.label ?? 'Warning'}
                        </Typography>
                        <Typography color="#6A767C" fontFamily="Open Sans" fontSize={16} fontWeight={400}>
                            Status
                        </Typography>
                    </Stack>
                    <Stack flex={1} spacing={0.75} justifyContent="center" height={84}>
                        {metrics.map((metric) => {
                            const isAlarm = (metric.severity ?? metric.status) === 'alarm';
                            const isWarning = (metric.severity ?? metric.status) === 'warning';
                            return (
                                <Stack
                                    key={metric.id ?? metric.name}
                                    height={16}
                                    direction="row"
                                    alignItems="center"
                                    spacing={1.5}
                                >
                                    {isAlarm ? (
                                        <NotificationsActive color="error" sx={{ fontSize: 16 }} />
                                    ) : isWarning ? (
                                        <Warning color="warning" sx={{ fontSize: 16 }} />
                                    ) : (
                                        <CheckCircleOutline sx={{ color: '#9CA5A9', fontSize: 16 }} />
                                    )}
                                    <Typography
                                        flex={1}
                                        color="#424E54"
                                        fontFamily="Open Sans"
                                        fontSize={12}
                                        fontWeight={600}
                                        sx={{ fontFeatureSettings: "'liga' off, 'clig' off" }}
                                        noWrap
                                    >
                                        {metric.name}
                                    </Typography>
                                    <Typography
                                        color="#424E54"
                                        fontFamily="Open Sans"
                                        fontSize={12}
                                        fontWeight={600}
                                        sx={{ fontFeatureSettings: "'liga' off, 'clig' off" }}
                                        whiteSpace="nowrap"
                                    >
                                        {metric.value} {metric.unit}
                                    </Typography>
                                    <ListItemTag
                                        label={metric.aggregation ?? metric.displayType ?? 'AVG'}
                                        backgroundColor={isAlarm ? '#D43D3D' : isWarning ? '#F5A623' : '#9AA5A9'}
                                        fontColor="#FFFFFF"
                                        sx={{
                                            '& .MuiTypography-root': {
                                                fontFamily: 'Open Sans',
                                                fontSize: '10px',
                                                fontWeight: 700,
                                                lineHeight: '16px',
                                                letterSpacing: '1px',
                                                textTransform: 'uppercase',
                                                fontFeatureSettings: "'liga' off, 'clig' off",
                                            },
                                        }}
                                    />
                                </Stack>
                            );
                        })}
                    </Stack>
                </Stack>
                <Stack height={48} px={3} justifyContent="center" className="border-bottom-1">
                    <Typography color="#424E54" fontFamily="Open Sans" fontSize={16} fontWeight={600}>
                        Notifications{' '}
                        <span style={{ fontWeight: 400 }}>{formatNotificationCounts(widgetData.statusCount)}</span>
                    </Typography>
                </Stack>
                {visibleNotifications.map((notification) => {
                    const occurredAt = formatOccurredAt(notification.occurredAt);
                    const time = notification.time ?? occurredAt.time;
                    const date = notification.date ?? occurredAt.date;
                    return (
                        <InfoListItem
                            key={notification.id}
                            data-testid={`environmental-stress-monitoring-notification-${notification.id}`}
                            divider="full"
                            sx={{ minHeight: 62, px: 3 }}
                            icon={getNotificationIcon(notification.type)}
                            title={
                                <Typography
                                    color="#424E54"
                                    fontFamily="Open Sans"
                                    fontSize={16}
                                    fontWeight={600}
                                    noWrap
                                >
                                    {notification.displayName ?? notification.title ?? ''}
                                </Typography>
                            }
                            leftComponent={
                                <Stack minWidth={92}>
                                    <Typography color="#424E54" fontFamily="Open Sans" fontSize={14} fontWeight={600}>
                                        {time}
                                    </Typography>
                                    <Typography color="#6A767C" fontFamily="Open Sans" fontSize={12}>
                                        {date}
                                    </Typography>
                                </Stack>
                            }
                            rightComponent={
                                <Typography
                                    color="#424E54"
                                    fontFamily="Open Sans"
                                    fontSize={16}
                                    fontWeight={600}
                                    whiteSpace="nowrap"
                                >
                                    {notification.value} {notification.unit}
                                </Typography>
                            }
                        />
                    );
                })}
            </Stack>
            <WidgetFooter
                widgetData={widgetData}
                ids={[]}
                mode={mode}
                actionBtnName="View All Environmental Stress Monitoring"
            />
        </Paper>
    );
};

export default Component;
